import './polyfills.js';
import { getWebInstrumentations, initializeFaro, FetchTransport, BaseTransport, getTransportBody, LogLevel, SessionInstrumentation } from '@grafana/faro-web-sdk';
import { CapacitorHttp, Capacitor } from '@capacitor/core';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/globals.css';
import App from './App';
import LogUtil from './utils/LogUtil';
import { loadConfig, getConfig } from './config';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { isCapacitor } from './services/googleAuthService';

LogUtil.init();

// Register service worker for PWA install support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { });
  });
}

loadConfig().then(async () => {
  // Initialize Grafana Faro — only when FARO_ENABLED=true in config.json
  // Set in prod config.json by CI/CD deploy-prod job only (not staging or dev)
  if (getConfig('FARO_ENABLED') === 'true') {
    // Capture native console.error BEFORE Faro patches it
    const nativeConsoleError = console.error;

    const isDev = !isCapacitor && window.location.hostname === 'localhost';

    const FARO_BASE = 'https://faro-collector-prod-us-east-3.grafana.net/collect/';
    // Mobile builds inject REACT_APP_FARO_COLLECTOR_KEY via CI secret (ANDROID_GRAFANA_KEY / IOS_GRAFANA_KEY).
    // Web prod-deploy hardcodes the web key since it ships via config.json, not build-time env.
    const COLLECTOR_URL = import.meta.env.REACT_APP_FARO_COLLECTOR_KEY
      ? `${FARO_BASE}${import.meta.env.REACT_APP_FARO_COLLECTOR_KEY}`
      : `${FARO_BASE}d07f3f0ec64ffffbfe77f8edc9a0c8a2`;

    const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
    const appName = isCapacitor
      ? (platform === 'android' ? 'exsports-android' : 'exsports-ios')
      : (getConfig('APP_NAME') || 'exsports-web');

    // On Capacitor, FetchTransport fails due to WebView CORS (capacitor://localhost origin rejected).
    // CapacitorHttp.request() makes native HTTP calls outside the WebView — no CORS enforcement.
    // We do NOT use CapacitorHttp: { enabled: true } globally because it breaks FormData uploads.
    class CapacitorFaroTransport extends BaseTransport {
      constructor() {
        super();
        this.name = 'capacitor-faro-transport';
      }
      async send(items) {
        if (!items?.length) return;
        try {
          const sessionId = this.metas?.value?.session?.id;
          const headers = { 'Content-Type': 'application/json' };
          if (sessionId) headers['x-faro-session-id'] = sessionId;
          await CapacitorHttp.request({
            method: 'POST',
            url: COLLECTOR_URL,
            headers,
            data: JSON.stringify(getTransportBody(items)),
          });
        } catch (e) {
          console.warn('[CapacitorFaroTransport] send failed:', e?.message ?? e);
        }
      }
      isBatched() { return true; }
      getIgnoreUrls() { return []; }
    }

    const faro = initializeFaro({
      transports: [
        isCapacitor
          ? new CapacitorFaroTransport()
          : (() => {
              const t = new FetchTransport({ url: COLLECTOR_URL, flushInterval: isDev ? 1000 : 30000 });
              const origSend = t.send.bind(t);
              t.send = (items) => (items?.length ? origSend(items) : Promise.resolve());
              return t;
            })(),
      ],
      app: {
        name: appName,
        version: '1.0.0',
        environment: isDev ? 'development' : 'production',
      },
      instrumentations: [
        new SessionInstrumentation(),
        ...getWebInstrumentations({
          consolesInstrumentation: {
            disabledLevels: [LogLevel.ERROR], // console.error → pushLog (not exception)
          },
        }),
        new TracingInstrumentation(),
      ],
      beforeSend: (item) => {
        // Real thrown exceptions
        if (item.type === 'exception') return item;

        // console.error and fetch/XHR/resource interceptor logs (level=error only)
        if (item.type === 'log' && item.payload?.level === 'error') return item;

        // Failed network traces only (4xx/5xx) — drop successful requests
        if (item.type === 'trace') {
          const spans = item.payload?.resourceSpans?.flatMap(
            rs => rs.scopeSpans?.flatMap(ss => ss.spans ?? []) ?? []
          ) ?? [];
          const hasFailed = spans.some(span => {
            const attr = span.attributes?.find(
              a => a.key === 'http.response.status_code' || a.key === 'http.status_code'
            );
            const code = attr?.value?.intValue ?? attr?.value?.doubleValue;
            return typeof code === 'number' && code >= 400;
          });
          return hasFailed ? item : null;
        }

        return null; // drop logs, measurements, events, successful traces
      },
    });

    // Re-route console.error → Faro log (not exception)
    // Uses nativeConsoleError captured before initializeFaro() to avoid calling
    // Faro's patched console.error which would trigger the exception path
    if (faro) {
      console.error = (...args) => {
        nativeConsoleError.apply(console, args);
        // A session-expiry redirect (apiUtil.js) cancels every other in-flight request on
        // the page — the resulting errors (Stream Chat auth, poll, etc.) are collateral
        // from that redirect, not real failures. Still shown locally via
        // nativeConsoleError above, just not shipped to Grafana as noise.
        if (window.__sessionRedirecting) return;
        faro.api.pushLog(
          [args.map(a => {
            if (typeof a === 'string') return a;
            if (a instanceof Error) return `${a.message} | stack: ${a.stack}`;
            try { return JSON.stringify(a); } catch { return String(a); }
          }).join(' ')],
          { level: LogLevel.ERROR }
        );
      };

      // Third-party scripts/beacons we don't control — content/ad blockers routinely
      // 404 or block these client-side, which isn't an app bug. Shared by the fetch
      // interceptor and the resource-error listener below so both stay in sync.
      const isNoisyThirdParty = (url) =>
        url.includes('google-analytics.com') ||
        url.includes('analytics.google.com') ||
        url.includes('googletagmanager.com') ||
        url.includes('accounts.google.com');

      // Intercept fetch() — log 4xx/5xx as errors
      // Skip Faro's own collector URL to avoid recursive log loops
      const nativeFetch = window.fetch;
      window.fetch = async (...args) => {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url ?? '';
        const isFaro = url.includes('faro-collector-prod-us-east-3.grafana.net') || url.includes('faro-collector');
        const isThirdParty = isNoisyThirdParty(url);
        // Health check timeouts/failures are expected (cold starts, offline) — the
        // server:unreachable banner handles UX; no need to spam Grafana with them.
        const isHealthCheck = url.includes('/exportsapi/health') || url.includes('/health');
        // Feature flags are non-critical and already default-safe when the endpoint
        // is unreachable (e.g. exsportscoreapi not routed on mobile CloudFront).
        const isFeatureFlags = url.includes('/features');
        try {
          const response = await nativeFetch(...args);
          // 401s from the main API are already logged as "[Session] Expired — redirecting to signin"
          // by apiUtil.js — suppress the duplicate "HTTP 401: ..." log here.
          const isSessionExpiry = response.status === 401 && url.includes('/exportsapi/');
          if (!response.ok && !isFaro && !isThirdParty && !isHealthCheck && !isFeatureFlags && !isSessionExpiry) {
            faro.api.pushLog(
              [`HTTP ${response.status}: ${url}`],
              { level: LogLevel.ERROR }
            );
          }
          return response;
        } catch (e) {
          // See the console.error override above — same redirect-collateral rationale.
          if (!isFaro && !isThirdParty && !isHealthCheck && !isFeatureFlags && !window.__sessionRedirecting) {
            faro.api.pushLog(
              [`Fetch failed: ${e?.message ?? e} — ${url}`],
              { level: LogLevel.ERROR }
            );
          }
          throw e;
        }
      };

      // Intercept XHR — log 4xx/5xx as errors
      const origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.addEventListener('load', () => {
          if (this.status >= 400) {
            faro.api.pushLog(
              [`HTTP ${this.status}: ${url}`],
              { level: LogLevel.ERROR }
            );
          }
        });
        return origOpen.apply(this, arguments);
      };

      // Catch resource load failures (image/script/link/media).
      // Third-party scripts (analytics, Google Sign-In) are routinely blocked by
      // ad/content blockers client-side — that's not an app bug, so skip them.
      // Note: the DOM 'error' event carries no HTTP status — it fires for any
      // load failure (network error, CORS, decode error, aborted load), not
      // just 404s, so the log message must not assert a specific status code.
      document.addEventListener('error', (e) => {
        const src = e.target?.src || e.target?.href;
        if (src && !isNoisyThirdParty(src)) {
          faro.api.pushLog(
            [`Resource load failed: ${src}`],
            { level: LogLevel.ERROR }
          );
        }
      }, true);
    }
  }

  // Global safety net — catch unhandled promise rejections (e.g. native plugin
  // errors that escape their try-catch) so they log to Faro and don't silently
  // crash the app on Android.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason instanceof Error
      ? `${reason.message} | stack: ${reason.stack}`
      : (typeof reason === 'string' ? reason : (() => { try { return JSON.stringify(reason); } catch { return String(reason); } })());
    console.error(`Unhandled promise rejection: ${msg}`);
    event.preventDefault();
  });

  window.onerror = (message, source, lineno, colno, error) => {
    const msg = error instanceof Error
      ? `${error.message} | stack: ${error.stack}`
      : String(message);
    console.error(`Global JS error: ${msg}`);
    return false;
  };

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      {isCapacitor ? (
        <App />
      ) : (
        <GoogleOAuthProvider clientId={getConfig('GOOGLE_CLIENT_ID')}>
          <App />
        </GoogleOAuthProvider>
      )}
    </React.StrictMode>
  );
});
